import { navigate, pathForCalendar } from '../routes.ts';
import { CallEnded } from './CallEnded.tsx';
import { CallGrid } from './CallGrid.tsx';
import { useCallSession } from './CallSession.tsx';
import { RoomCallCard } from './RoomCallCard.tsx';

/** `Call in #nordics`, or `Call with Marcus Yuen` for a direct message. */
export function callHeading(call: { title: string; direct?: boolean }): string {
  return call.direct ? `Call with ${call.title}` : `Call in #${call.title}`;
}

/** Opens a new calendar event from a call, or nothing for an event's own call:
 *  it already is a meeting. */
function bookFollowUp(call: { roomId: string; path: string }): (() => void) | null {
  if (call.roomId.startsWith('event:')) return null;
  const agoraId = /^\/h\/([^/]+)\//.exec(call.path)?.[1];
  if (!agoraId) return null;
  return () =>
    navigate(
      pathForCalendar(decodeURIComponent(agoraId), { date: null, eventId: null, creating: true }),
    );
}

/**
 * What a page shows of the tab's call when the call lives on that page: the
 * full call view when it is open, else a line saying the person is on it, with
 * Open. The media connection is not here -- `CallSession.tsx` holds it above
 * the routes, so this mounting and unmounting never joins or leaves anything.
 * Renders nothing for any other call, or none.
 */
export function CallSurface({
  roomId,
  className,
}: {
  /** The room key of the page's own call. */
  roomId: string;
  className?: string;
}) {
  const session = useCallSession();
  const call = session?.call;
  const ended = session?.ended;
  if (session && !call && ended && ended.target.roomId === roomId && !roomId.startsWith('event:')) {
    const bookEnded = bookFollowUp(ended.target);
    return (
      <CallEnded
        ended={ended}
        className={className}
        onBack={session.dismissEnded}
        onAgain={() => {
          session.dismissEnded();
          void session.start(ended.target, ended.calls);
        }}
        {...(bookEnded ? { onBook: bookEnded } : {})}
      />
    );
  }
  if (!session || !call || call.roomId !== roomId) return null;

  const book = bookFollowUp(call);
  if (session.focused && session.endpoint) {
    return (
      <CallGrid
        endpoint={session.endpoint}
        client={call.calls.client}
        sessionId={call.sessionId}
        participants={session.stats?.participants ?? []}
        joining={session.joining}
        heading={callHeading(call)}
        startedAt={call.startedAt}
        member
        lobby={session.lobby}
        onMinimize={() => session.setFocused(false)}
        {...(book ? { onBook: book } : {})}
        onLeft={session.leave}
        className={className}
      />
    );
  }

  return (
    <RoomCallCard
      session={session}
      startedAt={call.startedAt}
      {...(book ? { onBook: book } : {})}
      {...(className ? { className } : {})}
    />
  );
}
